import { useEffect } from "react";
import { createRoot } from "react-dom/client";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  Navigate,
} from "react-router-dom";
import * as Sentry from "@sentry/react";
import "src/assets/css/rdp-defaults.css";
import "tailwindcss/index.css";
import "@porchpass/ui/styles.css";
import "src/assets/css/global.css";
import { initializeGA } from "src/utils/analytics";
import DashboardPage from "src/pages/DashboardPage";
import InvitePage from "src/pages/InvitePage";
import ApplicationsPage from "src/pages/ApplicationsPage";
import ApplicationDetailPage from "src/pages/ApplicationDetailPage";
import EmployeesPage from "src/pages/EmployeesPage";
import { AppInitializer } from "src/initializers";
import { AppProviders } from "src/context";
import LoginPage from "./pages/LoginPage";
import LoginConfirmationPage from "./pages/LoginConfirmationPage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import EmailSentPage from "./pages/ForgotPasswordPage/EmailSentPage";
import AuthActionPage from "./pages/AuthActionPage";
import ProtectedLayout from "./layouts/ProtectedLayout";
import PublicLayout from "./layouts/PublicLayout";
import DealersPage from "./pages/DealersPage";
import ErrorPage from "./pages/ErrorPage";
import DealerSettings from "./pages/DealerSettings";
import ResourcesPage from "./pages/ResourcesPage";
import LocationSwitcherPage from "./pages/LocationSwitcherPage";
import { DealersProvider } from "./context/DealersContext";
import DealerUserInfoLayout from "./layouts/DealerUserInfoLayout";

Sentry.init({
  dsn: __SENTRY_DSN__,
  environment: __ENV__,
  release: __SENTRY_RELEASE__,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.browserProfilingIntegration(),
    Sentry.replayIntegration(),
    Sentry.consoleLoggingIntegration({
      levels: ["error"], // send this level and above to sentry
    }),
  ],
  replaysSessionSampleRate: 0.0,
  replaysOnErrorSampleRate: 1.0,
});

const App = () => {
  useEffect(() => {
    initializeGA();
  }, []);

  return (
    <AppProviders>
      <Router>
        <AppInitializer>
          <DealersProvider>
            <Routes>
              <Route element={<PublicLayout />}>
                <Route path="/" element={<Navigate to="/login" replace />} />
                <Route path={"login"}>
                  <Route index element={<LoginPage />} />
                  <Route
                    path={"confirmation"}
                    element={<LoginConfirmationPage />}
                  />
                  <Route
                    path={"forgot-password"}
                    element={<ForgotPasswordPage />}
                  />
                  <Route
                    path={"forgot-password/email-sent"}
                    element={<EmailSentPage />}
                  />
                </Route>
              </Route>
              <Route path={"auth/action"} element={<AuthActionPage />} />
              <Route path={"select-location"}>
                <Route element={<ProtectedLayout />}>
                  <Route index element={<LocationSwitcherPage />} />
                </Route>
              </Route>
              <Route path={":dealer_id"}>
                <Route element={<DealerUserInfoLayout />}>
                  <Route index element={<DashboardPage />} />
                  <Route path={"invite"} element={<InvitePage />} />
                  <Route path={"dashboard"} element={<DashboardPage />} />
                  <Route path={"applications"}>
                    <Route index element={<ApplicationsPage />} />
                    <Route path={":application_id"}>
                      <Route
                        index
                        element={<ApplicationDetailPage section="overview" />}
                      />
                      <Route
                        path="lenders"
                        element={<ApplicationDetailPage section="lenders" />}
                      />
                    </Route>
                  </Route>
                  <Route path={"employees"} element={<EmployeesPage />} />
                  <Route path={"dealers"} element={<DealersPage />} />
                  <Route path={"resources"} element={<ResourcesPage />} />
                  <Route path={"dealer-settings"}>
                    <Route
                      index
                      element={<Navigate to="preferences" replace />}
                    />
                    <Route path="preferences" element={<DealerSettings />} />
                    <Route path="lenders" element={<DealerSettings />} />
                  </Route>
                </Route>
              </Route>
              <Route path="error" element={<ErrorPage />} />
              <Route path="*" element={<Navigate to="/error" replace />} />
            </Routes>
          </DealersProvider>
        </AppInitializer>
      </Router>
    </AppProviders>
  );
};
const root = createRoot(document.getElementById("main") as HTMLElement);
root.render(<App />);
